import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState, type FormEvent } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { api, toApiMessage } from "../../api/client"
import { Button, ErrorState, Field, FormSection, LoadingState, PageHeader, StickyActionBar, showToast } from "../../components/ui"
import { useFamily } from "../../family/FamilyContext"
import { AudienceField, type Audience, type AudienceGroup } from "../family/AudienceField"

type ConfigKind = "currency" | "group" | "offer" | "ability" | "achievement" | "scheduled-grant"
type Currency = {
  id: string; name: string; code: string; symbol: string; icon: string; color: string; description: string
  transferEnabled: boolean; redeemProviderApprovalRequired: boolean; balanceVisibility?: "FAMILY" | "RESTRICTED"
  issueAudience: Audience; receiveAudience: Audience; deductAudience: Audience; redeemProviderAudience: Audience
}
type Ability = {
  id: string; name: string; description: string; icon: string; color?: string; rarity: string
  holderAudience: Audience; targetRequired: boolean; targetAudience?: Audience | null
  requireTargetApproval?: boolean; approverAudience?: Audience | null; approverMode?: "ANY_ONE" | "ALL"
  effect: "MANUAL" | "SKIP_TASK"; skipAdvancesRotation?: boolean
}
type Offer = {
  id: string; title: string; description: string; icon: string; color?: string; priceAmount: string
  priceCurrency: Currency; ability: Ability
}
type Achievement = {
  id: string; name: string; description: string; icon: string; color?: string; rarity: string
  rewardConfig?: { currency?: { currencyId: string; amount: string }; ability?: { definitionId: string; count: number } } | null
}
type ScheduledGrant = {
  id: string; name: string; enabled: boolean; schedule: { frequency?: string; interval?: number }; timezone: string
  audience: Audience; nextRunAt: string; currencyId?: string | null; currencyAmount?: string | null
  abilityDefinitionId?: string | null; abilityCount?: number | null
}
type ConfigItem = Currency | AudienceGroup | Offer | Ability | Achievement | ScheduledGrant

const labels: Record<ConfigKind, { one: string; eyebrow: string }> = {
  currency: { one: "валюта", eyebrow: "Валюты" },
  group: { one: "группа", eyebrow: "Группы" },
  offer: { one: "предложение", eyebrow: "Магазин" },
  ability: { one: "способность", eyebrow: "Способности" },
  achievement: { one: "достижение", eyebrow: "Достижения" },
  "scheduled-grant": { one: "выдача по расписанию", eyebrow: "Выдачи" }
}

function isoToLocal(value?: string | null) {
  if (!value) return ""
  const date = new Date(value)
  return new Date(date.valueOf() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function ConfigEditorPage() {
  const params = useParams<{ kind: string; id?: string }>()
  const kind = params.kind as ConfigKind
  const editing = Boolean(params.id)
  const navigate = useNavigate()
  const { activeFamily } = useFamily()
  if (!activeFamily) throw new Error("Семья не выбрана")
  const familyId = activeFamily.id
  const valid = Object.hasOwn(labels, kind)
  const groups = useQuery({ queryKey: ["v1", familyId, "groups"], queryFn: () => api.get<{ items: AudienceGroup[] }>(`/families/${familyId}/groups`) })
  const currencies = useQuery({ queryKey: ["v1", familyId, "currencies"], queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`), enabled: ["currency", "offer", "achievement", "scheduled-grant"].includes(kind) })
  const abilities = useQuery({ queryKey: ["v1", familyId, "abilities"], queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`), enabled: ["ability", "offer", "achievement", "scheduled-grant"].includes(kind) })
  const offers = useQuery({ queryKey: ["v1", familyId, "offers"], queryFn: () => api.get<{ items: Offer[] }>(`/families/${familyId}/offers`), enabled: kind === "offer" })
  const achievements = useQuery({ queryKey: ["v1", familyId, "achievements"], queryFn: () => api.get<{ items: Achievement[] }>(`/families/${familyId}/achievements`), enabled: kind === "achievement" })
  const scheduled = useQuery({ queryKey: ["v1", familyId, "scheduled-grants"], queryFn: () => api.get<{ items: ScheduledGrant[] }>(`/families/${familyId}/scheduled-grants`), enabled: kind === "scheduled-grant" })
  const queries = [groups, currencies, abilities, offers, achievements, scheduled]
  const source: ConfigItem[] =
    kind === "group" ? groups.data?.items ?? [] :
    kind === "currency" ? currencies.data?.items ?? [] :
    kind === "ability" ? abilities.data?.items ?? [] :
    kind === "offer" ? offers.data?.items ?? [] :
    kind === "achievement" ? achievements.data?.items ?? [] : scheduled.data?.items ?? []
  const item = editing ? source.find((candidate) => candidate.id === params.id) : undefined
  const loading = queries.some((query) => query.isLoading)
  const error = queries.find((query) => query.error)?.error

  if (!valid) return <div className="page-stack page-stack--form"><PageHeader title="Редактор не найден" /><ErrorState message="Неизвестный тип настройки." /></div>
  if (loading) return <div className="page-stack page-stack--form"><PageHeader eyebrow={labels[kind].eyebrow} title="Загрузка…" /><LoadingState rows={5} /></div>
  if (error) return <div className="page-stack page-stack--form"><PageHeader eyebrow={labels[kind].eyebrow} title="Не удалось открыть редактор" /><ErrorState message={toApiMessage(error)} retry={() => queries.forEach((query) => void query.refetch())} /></div>
  if (editing && !item) return <div className="page-stack page-stack--form"><PageHeader eyebrow={labels[kind].eyebrow} title="Настройка не найдена" /><ErrorState message="Она могла быть удалена или уже недоступна." /></div>

  const systemGroup = groups.data?.items.find((group) => group.isSystem)
  const everyone: Audience = { memberIds: [], groupIds: systemGroup ? [systemGroup.id] : [] }
  return (
    <div className="page-stack page-stack--form config-editor-page">
      <PageHeader
        eyebrow={labels[kind].eyebrow}
        title={editing ? `Редактировать: ${labels[kind].one}` : `Создать: ${labels[kind].one}`}
        description="Основные данные, правила доступа и действие сохранения собраны в одной последовательной форме."
      />
      <ConfigForm
        key={`${kind}:${params.id ?? "new"}`}
        familyId={familyId}
        kind={kind}
        item={item}
        everyone={everyone}
        currencies={currencies.data?.items ?? []}
        abilities={abilities.data?.items ?? []}
        onDone={() => navigate(-1)}
        onCancel={() => navigate(-1)}
      />
    </div>
  )
}

function ConfigForm({ familyId, kind, item, everyone, currencies, abilities, onDone, onCancel }: {
  familyId: string; kind: ConfigKind; item: ConfigItem | undefined; everyone: Audience; currencies: Currency[]; abilities: Ability[]
  onDone(): void; onCancel(): void
}) {
  const client = useQueryClient()
  const editing = Boolean(item)
  const currency = kind === "currency" ? item as Currency | undefined : undefined
  const group = kind === "group" ? item as AudienceGroup | undefined : undefined
  const ability = kind === "ability" ? item as Ability | undefined : undefined
  const offer = kind === "offer" ? item as Offer | undefined : undefined
  const achievement = kind === "achievement" ? item as Achievement | undefined : undefined
  const grant = kind === "scheduled-grant" ? item as ScheduledGrant | undefined : undefined
  const [issueAudience, setIssueAudience] = useState(currency?.issueAudience ?? everyone)
  const [receiveAudience, setReceiveAudience] = useState(currency?.receiveAudience ?? everyone)
  const [deductAudience, setDeductAudience] = useState(currency?.deductAudience ?? everyone)
  const [providerAudience, setProviderAudience] = useState(currency?.redeemProviderAudience ?? everyone)
  const [groupAudience, setGroupAudience] = useState<Audience>({ memberIds: group?.memberships.map((membership) => membership.memberId) ?? [], groupIds: [] })
  const [holderAudience, setHolderAudience] = useState(ability?.holderAudience ?? everyone)
  const [targetAudience, setTargetAudience] = useState(ability?.targetAudience ?? everyone)
  const [approverAudience, setApproverAudience] = useState(ability?.approverAudience ?? { memberIds: [], groupIds: [] })
  const [grantAudience, setGrantAudience] = useState(grant?.audience ?? everyone)
  const [targetRequired, setTargetRequired] = useState(ability?.targetRequired ?? false)
  const [rewardKind, setRewardKind] = useState<"currency" | "ability">(grant?.abilityDefinitionId ? "ability" : "currency")
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState("")

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    setSaving(true)
    setServerError("")
    try {
      let endpoint = ""
      let body: Record<string, unknown> = {}
      if (kind === "currency") {
        const icon = String(data.get("icon") || "🪙")
        endpoint = "currencies"
        body = {
          name: data.get("name"), symbol: icon, icon, description: data.get("description") || "",
          transferEnabled: data.get("transferEnabled") === "on",
          redeemProviderApprovalRequired: data.get("redeemProviderApprovalRequired") === "on",
          balanceVisibility: data.get("balanceVisibility"), issueAudience, receiveAudience, deductAudience,
          redeemProviderAudience: providerAudience,
          ...(!editing ? { code: `C${Date.now().toString(36)}`.toUpperCase().slice(0, 12), color: "#6D5EF7" } : {})
        }
      } else if (kind === "group") {
        endpoint = "groups"
        body = { name: data.get("name"), memberIds: groupAudience.memberIds }
      } else if (kind === "ability") {
        endpoint = "abilities"
        body = {
          name: data.get("name"), description: data.get("description"), icon: data.get("icon") || "✨",
          rarity: data.get("rarity"), holderAudience, targetRequired,
          targetAudience: targetRequired ? targetAudience : null,
          requireTargetApproval: data.get("requireTargetApproval") === "on",
          approverAudience: approverAudience.memberIds.length || approverAudience.groupIds.length ? approverAudience : null,
          approverMode: data.get("approverMode"), effect: data.get("effect"),
          skipAdvancesRotation: data.get("skipAdvancesRotation") === "on"
        }
      } else if (kind === "offer") {
        endpoint = "offers"
        body = {
          title: data.get("name"), description: data.get("description"), icon: data.get("icon") || "🎁",
          priceCurrencyId: data.get("currencyId"), priceAmount: data.get("amount"), abilityDefinitionId: data.get("abilityId")
        }
      } else if (kind === "achievement") {
        endpoint = "achievements"
        body = {
          name: data.get("name"), description: data.get("description"), icon: data.get("icon") || "🏆", rarity: data.get("rarity"),
          rewardConfig: data.get("rewardCurrencyId") || data.get("rewardAbilityId") ? {
            ...(data.get("rewardCurrencyId") && data.get("rewardCurrencyAmount") ? { currency: { currencyId: data.get("rewardCurrencyId"), amount: data.get("rewardCurrencyAmount") } } : {}),
            ...(data.get("rewardAbilityId") ? { ability: { definitionId: data.get("rewardAbilityId"), count: Number(data.get("rewardAbilityCount") || 1) } } : {})
          } : null
        }
      } else {
        endpoint = "scheduled-grants"
        body = {
          name: data.get("name"), enabled: data.get("enabled") === "on",
          schedule: { frequency: data.get("frequency"), interval: Number(data.get("interval") || 1) },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, audience: grantAudience,
          nextRunAt: new Date(String(data.get("nextRunAt"))).toISOString(),
          ...(rewardKind === "currency" ? { currencyId: data.get("currencyId"), currencyAmount: data.get("amount") } : { abilityDefinitionId: data.get("abilityId"), abilityCount: Number(data.get("amount") || 1) })
        }
      }
      if (editing) await api.patch(`/families/${familyId}/${endpoint}/${item!.id}`, body)
      else await api.post(`/families/${familyId}/${endpoint}`, body)
      await client.invalidateQueries({ queryKey: ["v1", familyId] })
      showToast(editing ? "Изменения сохранены" : "Создано")
      onDone()
    } catch (error) {
      setServerError(toApiMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const name = currency?.name ?? group?.name ?? ability?.name ?? offer?.title ?? achievement?.name ?? grant?.name ?? ""
  const description = currency?.description ?? ability?.description ?? offer?.description ?? achievement?.description ?? ""
  const icon = currency?.icon ?? ability?.icon ?? offer?.icon ?? achievement?.icon ?? (kind === "currency" ? "🪙" : kind === "offer" ? "🎁" : kind === "achievement" ? "🏆" : "✨")

  return (
    <form className="form-stack config-editor-form" onSubmit={submit}>
      {serverError ? <ErrorState title="Не удалось сохранить" message={serverError} /> : null}
      <FormSection title="Основное">
        <div className="form-grid">
          <Field label="Название"><input name="name" required maxLength={120} autoFocus defaultValue={name} /></Field>
          {kind !== "group" && kind !== "scheduled-grant" ? <Field label="Эмодзи"><input name="icon" required maxLength={16} defaultValue={icon} /></Field> : null}
        </div>
        {kind !== "group" && kind !== "scheduled-grant" ? <Field label="Описание"><textarea name="description" rows={3} defaultValue={description} /></Field> : null}
      </FormSection>

      {kind === "group" ? <FormSection title="Участники"><AudienceField label="Кто входит" value={groupAudience} onChange={setGroupAudience} membersOnly /></FormSection> : null}
      {kind === "currency" ? (
        <>
          <FormSection title="Права валюты" description="Группы и отдельные люди для каждого действия.">
            <AudienceField label="Кто может начислять" value={issueAudience} onChange={setIssueAudience} />
            <AudienceField label="Кто может получать и тратить" value={receiveAudience} onChange={setReceiveAudience} />
            <AudienceField label="Кто может назначать штрафы" value={deductAudience} onChange={setDeductAudience} />
            <AudienceField label="Кто принимает оплату" value={providerAudience} onChange={setProviderAudience} />
          </FormSection>
          <FormSection title="Правила">
            <label className="check-row"><input name="transferEnabled" type="checkbox" defaultChecked={currency?.transferEnabled ?? true} /><span>Можно передавать</span></label>
            <Field label="Кто видит балансы"><select name="balanceVisibility" defaultValue={currency?.balanceVisibility ?? "FAMILY"}><option value="FAMILY">Вся семья</option><option value="RESTRICTED">Только владелец и участники правил</option></select></Field>
            <label className="check-row"><input name="redeemProviderApprovalRequired" type="checkbox" defaultChecked={currency?.redeemProviderApprovalRequired ?? false} /><span>Трата требует подтверждения выбранного человека</span></label>
          </FormSection>
        </>
      ) : null}
      {kind === "ability" ? (
        <>
          <FormSection title="Доступ и эффект">
            <AudienceField label="Кому можно выдавать" value={holderAudience} onChange={setHolderAudience} />
            <div className="form-grid">
              <Field label="Редкость"><select name="rarity" defaultValue={ability?.rarity ?? "COMMON"}><option value="COMMON">Обычная</option><option value="RARE">Редкая</option><option value="EPIC">Эпическая</option><option value="LEGENDARY">Легендарная</option></select></Field>
              <Field label="Эффект"><select name="effect" defaultValue={ability?.effect ?? "MANUAL"}><option value="MANUAL">Ручной</option><option value="SKIP_TASK">Пропустить свою задачу</option></select></Field>
            </div>
            <label className="check-row"><input type="checkbox" checked={targetRequired} onChange={(event) => setTargetRequired(event.target.checked)} /><span>При использовании нужно выбрать человека</span></label>
            {targetRequired ? <AudienceField label="На кого можно использовать" value={targetAudience} onChange={setTargetAudience} /> : null}
          </FormSection>
          <FormSection title="Подтверждение" optional>
            <label className="check-row"><input name="requireTargetApproval" type="checkbox" defaultChecked={ability?.requireTargetApproval ?? false} /><span>Нужно подтверждение выбранного человека</span></label>
            <AudienceField label="Кто ещё подтверждает" value={approverAudience} onChange={setApproverAudience} />
            <Field label="Сколько подтверждений нужно"><select name="approverMode" defaultValue={ability?.approverMode ?? "ANY_ONE"}><option value="ANY_ONE">Одного достаточно</option><option value="ALL">Нужны все</option></select></Field>
            <label className="check-row"><input name="skipAdvancesRotation" type="checkbox" defaultChecked={ability?.skipAdvancesRotation ?? true} /><span>Пропуск передаёт очередь следующему</span></label>
          </FormSection>
        </>
      ) : null}
      {kind === "offer" ? (
        <FormSection title="Что выдаём и за сколько">
          <Field label="Способность"><select name="abilityId" required defaultValue={offer?.ability.id ?? ""}><option value="">Выберите способность</option>{abilities.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field>
          <div className="form-grid">
            <Field label="Цена"><input name="amount" required inputMode="numeric" pattern="[0-9]+" defaultValue={offer?.priceAmount ?? ""} /></Field>
            <Field label="Валюта цены"><select name="currencyId" required defaultValue={offer?.priceCurrency.id ?? ""}><option value="">Выберите валюту</option>{currencies.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field>
          </div>
        </FormSection>
      ) : null}
      {kind === "achievement" ? (
        <>
          <FormSection title="Редкость"><Field label="Редкость"><select name="rarity" defaultValue={achievement?.rarity ?? "COMMON"}><option value="COMMON">Обычная</option><option value="RARE">Редкая</option><option value="EPIC">Эпическая</option><option value="LEGENDARY">Легендарная</option></select></Field></FormSection>
          <FormSection title="Бонус" optional>
            <div className="form-grid"><Field label="Валюта"><select name="rewardCurrencyId" defaultValue={achievement?.rewardConfig?.currency?.currencyId ?? ""}><option value="">Без валюты</option>{currencies.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field><Field label="Количество"><input name="rewardCurrencyAmount" inputMode="numeric" defaultValue={achievement?.rewardConfig?.currency?.amount ?? ""} /></Field></div>
            <div className="form-grid"><Field label="Способность"><select name="rewardAbilityId" defaultValue={achievement?.rewardConfig?.ability?.definitionId ?? ""}><option value="">Без способности</option>{abilities.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field><Field label="Количество"><input name="rewardAbilityCount" type="number" min="1" max="100" defaultValue={achievement?.rewardConfig?.ability?.count ?? 1} /></Field></div>
          </FormSection>
        </>
      ) : null}
      {kind === "scheduled-grant" ? (
        <>
          <FormSection title="Кому"><AudienceField label="Получатели" value={grantAudience} onChange={setGrantAudience} /></FormSection>
          <FormSection title="Что выдаём">
            <Field label="Тип награды"><select value={rewardKind} onChange={(event) => setRewardKind(event.target.value as typeof rewardKind)}><option value="currency">Валюта</option><option value="ability">Способность</option></select></Field>
            {rewardKind === "currency" ? <Field label="Валюта"><select name="currencyId" required defaultValue={grant?.currencyId ?? ""}><option value="">Выберите валюту</option>{currencies.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field> : <Field label="Способность"><select name="abilityId" required defaultValue={grant?.abilityDefinitionId ?? ""}><option value="">Выберите способность</option>{abilities.map((entry) => <option key={entry.id} value={entry.id}>{entry.icon} {entry.name}</option>)}</select></Field>}
            <Field label="Количество"><input name="amount" required inputMode="numeric" defaultValue={grant?.currencyAmount ?? grant?.abilityCount ?? ""} /></Field>
          </FormSection>
          <FormSection title="Расписание">
            <div className="form-grid"><Field label="Повтор"><select name="frequency" defaultValue={grant?.schedule.frequency ?? "MONTHLY"}><option value="DAILY">Каждый день</option><option value="WEEKLY">Каждую неделю</option><option value="MONTHLY">Каждый месяц</option></select></Field><Field label="Интервал"><input name="interval" type="number" min="1" max="365" defaultValue={grant?.schedule.interval ?? 1} /></Field></div>
            <Field label="Следующая выдача"><input name="nextRunAt" type="datetime-local" required defaultValue={isoToLocal(grant?.nextRunAt)} /></Field>
            <label className="check-row"><input name="enabled" type="checkbox" defaultChecked={grant?.enabled ?? true} /><span>Расписание активно</span></label>
          </FormSection>
        </>
      ) : null}

      <StickyActionBar><Button type="button" variant="ghost" onClick={onCancel}>Отмена</Button><Button type="submit" loading={saving}>{editing ? "Сохранить" : "Создать"}</Button></StickyActionBar>
    </form>
  )
}
